import * as React from "react";
import { useNavigate } from "react-router-dom";
import { RenewOpportunityInput, usePutV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberMutation } from "randmar-api-client";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Opportunity } from "../../stores/opportunities/Opportunity";
import { setUserSelectedOpportunity } from "../../utils/LocalStorageUtils";
import CenteredLoader from "../util/CenteredLoader";
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from "randmarcomps";

interface RenewOpportunityWrapperProps {
  opportunity: Opportunity;
  disabled?: boolean;
  onUpdated?: () => void;
  children: React.ReactNode;
}

const toDateInputValue = (value?: string) => {
  const raw = (value || "").trim();
  if (!raw) return "";
  if (/^\\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  return raw;
};

const RenewOpportunityWrapper: React.FC<RenewOpportunityWrapperProps> = ({ opportunity, disabled, children, onUpdated }) => {
  const { applicationId } = useManufacturer();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [open, setOpen] = React.useState(false);
  const [renewed, setRenewed] = React.useState(false);

  const [bidNumber, setBidNumber] = React.useState("");
  const [bidTitle, setBidTitle] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [quoteOnly, setQuoteOnly] = React.useState(false);

  const [renewOpportunity, renewOpportunityState] = usePutV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberMutation();

  const handleOpen = React.useCallback(() => {
    setBidNumber(opportunity.bidNumber || "");
    setBidTitle(opportunity.opportunity || "");
    setStartDate(toDateInputValue(opportunity.startDate));
    setEndDate(toDateInputValue(opportunity.endDate));
    setQuoteOnly(false);
    setOpen(true);
  }, [opportunity.bidNumber, opportunity.endDate, opportunity.opportunity, opportunity.startDate]);

  const handleConfirm = React.useCallback(async () => {
    if (!opportunity.bidNumber) return;

    const renewInput: RenewOpportunityInput = {
      BidNumber: bidNumber,
      BidTitle: bidTitle,
      StartDate: startDate,
      EndDate: endDate,
      QuoteOnly: quoteOnly,
    };

    try {
      const success = await renewOpportunity({
        routeManufacturerId: applicationId,
        bidNumber: opportunity.bidNumber,
        renewOpportunityInput: renewInput,
      }).unwrap();

      if (success) {
        toast({ variant: "success", title: "Renewed", description: "Opportunity renewed." });
        setUserSelectedOpportunity(bidNumber);
        setOpen(false);
        setRenewed(true);
        onUpdated?.();
      } else {
        toast({ variant: "destructive", title: "Error", description: "Opportunity could not be renewed." });
      }
    } catch (error) {
      console.error("Failed to renew opportunity:", error);
      toast({ variant: "destructive", title: "Error", description: "Opportunity could not be renewed." });
    }
  }, [applicationId, bidNumber, bidTitle, endDate, onUpdated, opportunity.bidNumber, quoteOnly, renewOpportunity, startDate, toast]);

  const isBusy = renewOpportunityState.isLoading;
  const isInvalid = !startDate || !endDate || !bidTitle || !bidNumber || startDate > endDate || disabled;

  return (
    <>
      <div className="contents" onClick={() => !disabled && handleOpen()}>
        {children}
      </div>

      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isBusy && setOpen(false)}>
        <DialogContent className="sm:max-w-[560px]">
          {isBusy ? (
            <CenteredLoader text="Renewing…" variant="plain" className="min-h-[260px]" />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Renew opportunity</DialogTitle>
              </DialogHeader>

              <Alert variant="default" className="rounded-2xl">
                <AlertDescription>
                  Creates a new opportunity with the same resellers and rebates on the same part numbers.
                </AlertDescription>
              </Alert>

              <div className="grid gap-4 py-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="renew-bid-number">Opportunity number</Label>
                  <Input id="renew-bid-number" value={bidNumber} onChange={(e) => setBidNumber(e.target.value)} className="h-11 rounded-xl" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="renew-bid-title">Opportunity title</Label>
                  <Input id="renew-bid-title" value={bidTitle} onChange={(e) => setBidTitle(e.target.value)} className="h-11 rounded-xl" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="renew-start-date">Start date</Label>
                  <Input id="renew-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-11 rounded-xl" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="renew-end-date">End date</Label>
                  <Input id="renew-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-11 rounded-xl" />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox id="renew-quote-only" checked={quoteOnly} onCheckedChange={(checked) => setQuoteOnly(Boolean(checked))} />
                <Label htmlFor="renew-quote-only" className="cursor-pointer text-[15px] text-slate-700">
                  Quote only
                </Label>
              </div>

              <DialogFooter className="mt-4 flex flex-wrap justify-between gap-2">
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" className="rounded-xl" disabled={isInvalid} onClick={handleConfirm}>
                  Confirm
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={renewed} onOpenChange={(isOpen) => !isOpen && setRenewed(false)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Opportunity renewed</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-[15px] text-slate-700">
            {opportunity.opportunity || opportunity.bidNumber} was successfully renewed.
          </div>
          <DialogFooter className="flex flex-wrap justify-between gap-2">
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setRenewed(false)}>
              Close
            </Button>
            <Button
              type="button"
              className="rounded-xl"
              onClick={() => navigate(Path.opportunity(applicationId, bidNumber))}
              disabled={!bidNumber}
            >
              View new opportunity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RenewOpportunityWrapper;
